import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { onAuthStateChanged, type User } from 'firebase/auth';
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import type { DateFormat, TimeFormat } from '@dosfilos/shared';
import { auth, db } from '../lib/firebase';
import { resolveDomainTenant } from '../lib/resolve-tenant-by-domain';
import { useTenantStore } from '../stores/tenant.store';
import { usePortalViewStore } from '../stores/portal-view.store';
import { applyUserChange } from '../lib/session';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isPlatformAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  isPlatformAdmin: false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  // Quién era la persona en el evento anterior (`undefined` antes del primero).
  const queryClient = useQueryClient();
  const lastUid = useRef<string | null | undefined>(undefined);
  const {
    setCurrentTenant,
    setAvailableTenants,
    clearTenant,
    currentTenantId,
    setTenantTheme,
    setCurrentTenantTimeZone,
    setCurrentTenantDateFormat,
    setCurrentTenantTimeFormat,
  } = useTenantStore();

  useEffect(() => {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      // Otra persona (o ninguna): nada de la caché de la anterior llega a la
      // siguiente, ni por un instante (lib/session). Antes que todo lo demás.
      lastUid.current = applyUserChange(queryClient, lastUid.current, firebaseUser?.uid ?? null);
      // «Ver portal»: la vista es de quien la abrió. Cerrar sesión o entrar con
      // otra cuenta en la misma pestaña la borra (stores/portal-view.store).
      usePortalViewStore.getState().setSessionUid(firebaseUser?.uid ?? null);
      setUser(firebaseUser);

      if (firebaseUser) {
        setLoading(true); // Reset loading while we fetch tenants/membership
        // Load tenants
        try {
          const tenants: {
            id: string;
            name: string;
            slug: string;
            primaryColor?: string;
            logoUrl?: string;
            timezone?: string;
            dateFormat?: DateFormat;
            timeFormat?: TimeFormat;
          }[] = [];
          let firstMembership: { tenantId: string; membership: Record<string, unknown> } | null =
            null;

          // 1. FORCE REFRESH & Verify Admin Status
          const token = await firebaseUser.getIdTokenResult(true);

          // Check claim
          let isAdmin = token.claims['platformRole'] === 'platformAdmin';

          // Fallback: Check platformUsers document (in case claims haven't propagated)
          if (!isAdmin) {
            try {
              const userDoc = await getDoc(doc(db, 'platformUsers', firebaseUser.uid));
              if (userDoc.exists() && userDoc.data()?.['role'] === 'platformAdmin') {
                isAdmin = true;
                console.log('[AuthProvider] Admin verified via platformUsers document.');
              }
            } catch (e) {
              // Non-admin will probably fail this read, that's fine
            }
          }

          setIsPlatformAdmin(isAdmin);
          console.log('[AuthProvider] Admin Status:', isAdmin, '| Claims:', token.claims);

          // 2. ATTEMPT 1: Direct List — PLATFORM ADMINS ONLY. The `tenants`
          // collection is public-readable (landing pages need it), so this
          // query succeeds for ANY user; without the gate, every teacher saw
          // every seminary in the org switcher. Non-admins resolve their
          // tenants via their memberships (attempts 2/3).
          try {
            if (!isAdmin) throw new Error('non-admin: skip direct tenant list');
            const tenantsSnap = await getDocs(collection(db, 'tenants'));
            if (!tenantsSnap.empty) {
              tenantsSnap.docs.forEach((d) => {
                const data = d.data();
                tenants.push({
                  id: d.id,
                  name: data['name'] || d.id,
                  slug: data['slug'] || d.id,
                  primaryColor: data['config']?.['primaryColor'],
                  logoUrl: data['config']?.['logoUrl'],
                  timezone: data['config']?.['timezone'],
                  dateFormat: data['config']?.['dateFormat'],
                  timeFormat: data['config']?.['timeFormat'],
                });
              });
              console.log('[AuthProvider] Direct tenants fetch success:', tenants.length);
            }
          } catch (tErr: any) {
            console.log(
              '[AuthProvider] Direct tenants fetch restricted (Security rules). Switching to membership search...',
            );
          }

          // 3. ATTEMPT 2: Search via Index (tenantsIndex)
          // We do this if we haven't found everything yet
          try {
            const indexSnap = await getDocs(collection(db, 'tenantsIndex'));
            console.log('[AuthProvider] tenantsIndex items:', indexSnap.size);

            const membershipPromises = indexSnap.docs.map(async (tenantDoc) => {
              const tenantId = tenantDoc.id;
              if (tenants.some((t) => t.id === tenantId)) return null; // Already listed

              const mSnap = await getDoc(
                doc(db, `tenants/${tenantId}/memberships`, firebaseUser.uid),
              );
              if (mSnap.exists()) {
                return {
                  tenantId,
                  name: tenantDoc.data()['name'] || tenantId,
                  slug: tenantDoc.data()['slug'] || tenantId,
                  primaryColor: tenantDoc.data()['primaryColor'] as string | undefined,
                  // The index now carries the tenant zone + date format so a student
                  // (who can't read the tenant doc) gets them here — see
                  // FirestoreTenantRepository.
                  timezone: tenantDoc.data()['timezone'] as string | undefined,
                  dateFormat: tenantDoc.data()['dateFormat'] as DateFormat | undefined,
                  timeFormat: tenantDoc.data()['timeFormat'] as TimeFormat | undefined,
                  membership: mSnap.data(),
                };
              }
              return null;
            });

            const results = await Promise.all(membershipPromises);
            results.forEach((res) => {
              if (res) {
                tenants.push({
                  id: res.tenantId,
                  name: res.name,
                  slug: res.slug,
                  ...(res.primaryColor ? { primaryColor: res.primaryColor } : {}),
                  ...(res.timezone ? { timezone: res.timezone } : {}),
                  ...(res.dateFormat ? { dateFormat: res.dateFormat } : {}),
                  ...(res.timeFormat ? { timeFormat: res.timeFormat } : {}),
                });
                if (!firstMembership)
                  firstMembership = { tenantId: res.tenantId, membership: res.membership };
              }
            });
          } catch (idxErr) {
            console.warn('[AuthProvider] tenantsIndex restricted or empty.');
          }

          // 4. ATTEMPT 3: Collection Group (Last resort, might fail index)
          if (tenants.length === 0) {
            try {
              const snap = await getDocs(
                query(collectionGroup(db, 'memberships'), where('uid', '==', firebaseUser.uid)),
              );
              snap.docs.forEach((d) => {
                const tenantDoc = d.ref.parent.parent;
                if (!tenantDoc) return;
                if (!tenants.some((t) => t.id === tenantDoc.id)) {
                  tenants.push({
                    id: tenantDoc.id,
                    name: d.data()['tenantName'] || tenantDoc.id,
                    slug: d.data()['tenantSlug'] || tenantDoc.id,
                  });
                }
                if (!firstMembership)
                  firstMembership = { tenantId: tenantDoc.id, membership: d.data() };
              });
            } catch (cgErr) {
              console.warn('[AuthProvider] Membership search failed (index missing).');
            }
          }

          // 4.5 CUSTOM-DOMAIN SCOPING — the hostname is the AUTHORITY.
          // On a seminary's own custom domain (e.g. sebex.cl) restrict the
          // available tenants to THAT seminary only, so a user who belongs to
          // several seminaries can never see another's data under this domain.
          //   - resolved   → scope to that tenant (non-member ⇒ [] ⇒ /no-access)
          //   - unresolved → FAIL CLOSED: custom host we couldn't resolve ⇒ []
          //                  ⇒ /no-access (never trust localStorage here)
          //   - platform   → no scoping (tenant from membership)
          // SCOPING (security): on a custom domain, restrict availableTenants to
          // the resolved tenant; fail closed on unresolved. `currentDomain` (the
          // UX role for routing) is set separately by <DomainResolver/>, which
          // runs independent of auth — resolveDomainTenant() caches so this is
          // one fetch, not two.
          const domain = await resolveDomainTenant();
          if (domain.mode === 'resolved') {
            const scoped = tenants.filter((t) => t.id === domain.tenantId);
            tenants.length = 0;
            tenants.push(...scoped);
          } else if (domain.mode === 'unresolved') {
            tenants.length = 0; // fail closed
          }

          // 5. FINALIZE
          console.log('[AuthProvider] Final organizations found:', tenants.length);
          setAvailableTenants(tenants);

          // Apply the tenant's primary color to the theme store so that
          // all users (including students) see the correct brand color
          // immediately after login, without visiting the settings page.
          const activeTenant = tenants.find((t) => t.id === currentTenantId) ?? tenants[0];
          if (activeTenant?.primaryColor) {
            setTenantTheme({
              primaryColor: activeTenant.primaryColor,
              name: activeTenant.name,
              slug: activeTenant.slug,
              ...(activeTenant.logoUrl ? { logoUrl: activeTenant.logoUrl } : {}),
            });
          }
          // Active tenant's zone for date display. The domain resolver is the
          // portal/hostname authority (a student never reads the tenant `config`);
          // the direct-fetch `config.timezone` covers admin/staff. Null → the
          // display falls back to the default zone via useTenantTimeZone().
          const resolvedTz =
            (domain.mode === 'resolved' ? domain.timezone : undefined) ??
            activeTenant?.timezone ??
            null;
          setCurrentTenantTimeZone(resolvedTz);
          // Same sources for the numeric date ordering. Null → dd-mm-yyyy default.
          const resolvedFmt =
            (domain.mode === 'resolved' ? domain.dateFormat : undefined) ??
            activeTenant?.dateFormat ??
            null;
          setCurrentTenantDateFormat(resolvedFmt);
          const resolvedTimeFmt =
            (domain.mode === 'resolved' ? domain.timeFormat : undefined) ??
            activeTenant?.timeFormat ??
            null;
          setCurrentTenantTimeFormat(resolvedTimeFmt);

          if (tenants.length > 0) {
            const isValid = tenants.some((t) => t.id === currentTenantId);
            const target = tenants.find((t) => t.id === currentTenantId) || tenants[0];

            if (target) {
              // Always refresh the membership data so roles are up-to-date.
              // The store persists only currentTenantId, not the membership,
              // so we must reload it on every session start.
              let membershipData: Record<string, unknown> | null =
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (firstMembership as any)?.tenantId === target.id
                  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (firstMembership as any)?.membership
                  : null;

              if (!membershipData) {
                // Reload from Firestore for the already-selected tenant
                try {
                  const mSnap = await getDoc(
                    doc(db, `tenants/${target.id}/memberships`, firebaseUser.uid),
                  );
                  if (mSnap.exists()) {
                    membershipData = mSnap.data();
                    console.log(
                      '[AuthProvider] Reloaded membership for existing tenant:',
                      target.id,
                      membershipData,
                    );
                  }
                } catch (mErr) {
                  console.warn('[AuthProvider] Could not reload membership:', mErr);
                }
              }

              const m = membershipData || {
                roles: isAdmin ? ['platformAdmin'] : [],
                status: 'active',
              };
              console.log(
                '[AuthProvider] Setting tenant:',
                target.id,
                '| Roles:',
                (m as Record<string, unknown>)['roles'],
              );
              setCurrentTenant(target.id, m as unknown as Parameters<typeof setCurrentTenant>[1]);

              if (!isValid) {
                console.log('[AuthProvider] Auto-selecting new tenant:', target.id);
              }
            }
          }
        } catch (err) {
          console.error('[AuthProvider] Global load error:', err);
        }
      } else {
        setIsPlatformAdmin(false);
        clearTenant();
      }

      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, isPlatformAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
